import React, { useEffect, useRef, useState } from 'react';
import { AttachmentSelector, SendButton, type SendButtonProps, useChannelStateContext, useMessageComposer, useStateStore } from 'stream-chat-react';
import { FontAwesome } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { fetchChannelTemplates, sendMessageViaTemplate } from '@/services/universe/client';
import { useChannelData } from '@/hooks/useChannelData';
import { getStatusPermissions } from '@/utils/statusPermissions';
import type { UniverseStreamChannel } from '@/services/stream/types';

export interface Template {
    id: string;
    name: string | null;
    language: string | null;
    text: string;
}

const textSelector = (state: { text: string }) => ({ hasText: !!state.text });

const POPOVER_WIDTH = 380;
const POPOVER_HEIGHT = 420;

const WebTemplatePickerButton = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { channel } = useChannelStateContext();
    const messageComposer = useMessageComposer();
    const { hasText } = useStateStore(messageComposer.textComposer.state, textSelector);

    useEffect(() => {
        if (modalVisible && channel?.id) {
            setPage(1);
            loadTemplates(1);
        }
    }, [modalVisible, channel?.id]);

    const loadTemplates = async (pageNum: number) => {
        if (!channel?.id) return;

        pageNum === 1 ? setLoading(true) : setLoadingMore(true);

        try {
            const response = await fetchChannelTemplates(channel.id, pageNum);
            if (response?.success && response?.data) {
                const newData = Array.isArray(response.data)
                    ? response.data
                    : Object.values(response.data);

                setTemplates(prev => pageNum === 1 ? newData : [...prev, ...newData]);

                const paginationSource = response.meta ?? response.pagination;
                if (paginationSource) {
                    setLastPage(paginationSource.last_page);
                    setPage(paginationSource.current_page);
                }
            }
        } catch (error) {
            console.error('Error loading templates:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleOpen = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const left = Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8);
            const top = rect.top - POPOVER_HEIGHT - 8;
            setPopoverPos({ top, left });
        }
        setModalVisible(true);
    };

    const handleSelectTemplate = async (template: Template) => {
        if (!channel?.id || sending) return;
        setSending(true);
        try {
            await sendMessageViaTemplate(channel.id, template);
            setModalVisible(false);
        } finally {
            setSending(false);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && !loadingMore && page < lastPage) {
            loadTemplates(page + 1);
        }
    };

    const filteredTemplates = templates.filter(t =>
        (t.text ?? '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <button
                ref={buttonRef}
                onClick={handleOpen}
                title="Template Messages"
                style={{
                    width: hasText ? 0 : 32,
                    opacity: hasText ? 0 : 1,
                    overflow: 'hidden',
                    transition: 'opacity 0.2s ease, width 0.2s ease',
                    pointerEvents: hasText ? 'none' : 'auto',
                    flexShrink: 0,
                    height: 32,
                    borderRadius: '50%',
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 2,
                    marginRight: 2,
                    padding: 0,
                    color: Colors.brand,
                }}
            >
                <FontAwesome name="file-text" size={17} color={Colors.brand} />
            </button>

            {modalVisible && (
                <div
                    onClick={() => setModalVisible(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: 'absolute',
                            top: popoverPos.top,
                            left: popoverPos.left,
                            width: POPOVER_WIDTH,
                            height: POPOVER_HEIGHT,
                            background: 'white',
                            borderRadius: 12,
                            border: '1px solid #E5E7EB',
                            padding: 16,
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <span style={{ fontSize: 18, fontWeight: 'bold', color: '#111827' }}>Template Messages</span>
                            <button
                                onClick={() => setModalVisible(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 18, lineHeight: 1 }}
                            >
                                <FontAwesome name="times" size={20} color="#6B7280" />
                            </button>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#F3F4F6',
                            borderRadius: 8,
                            padding: '0 12px',
                            marginBottom: 16,
                            height: 40,
                        }}>
                            <FontAwesome name="search" size={14} color="#6B7280" style={{ marginRight: 8 }} />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    background: 'transparent',
                                    outline: 'none',
                                    fontSize: 16,
                                    color: '#111827',
                                }}
                            />
                        </div>

                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                opacity: sending ? 0.5 : 1,
                                pointerEvents: sending ? 'none' : 'auto',
                            }}
                            onScroll={handleScroll}
                        >
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6B7280' }}>
                                    Loading templates...
                                </div>
                            ) : filteredTemplates.length === 0 ? (
                                <div style={{ textAlign: 'center', marginTop: 20, color: '#6B7280' }}>
                                    {searchQuery ? 'No templates found' : 'No templates available'}
                                </div>
                            ) : (
                                filteredTemplates.map((template, index) => (
                                    <div
                                        key={`${template.id}-${index}`}
                                        onClick={() => handleSelectTemplate(template)}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#F9FAFB')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                        style={{
                                            border: '1px solid #E5E7EB',
                                            borderRadius: 8,
                                            padding: 12,
                                            marginBottom: 8,
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            color: '#111827',
                                        }}
                                    >
                                        {template.text}
                                    </div>
                                ))
                            )}
                            {loadingMore && (
                                <div style={{ textAlign: 'center', padding: 10, color: '#6B7280' }}>
                                    Loading more...
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const WebAttachmentSelector = () => {
    const { channel } = useChannelStateContext();
    const { status } = useChannelData(channel as UniverseStreamChannel);
    const { canSendMedia } = getStatusPermissions(status);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {canSendMedia && <AttachmentSelector />}
            <WebTemplatePickerButton />
        </div>
    );
};

// Send button override for <Channel> that also honours the channel-status
// permissions (blocks sending text that was typed before the status flipped).
export const StatusAwareSendButton = (props: SendButtonProps) => {
    const { channel } = useChannelStateContext();
    const { status } = useChannelData(channel as UniverseStreamChannel);
    const { canSendMessage } = getStatusPermissions(status);

    return <SendButton {...props} disabled={props.disabled || !canSendMessage} />;
};
